import actionIcon from 'assets/icons/PPSIcons/actionIcon.svg';
import deleteIcon from 'assets/icons/PPSIcons/deleteIcon.svg';
import editIcon from 'assets/icons/PPSIcons/editIcon.svg';
import searchIcon from 'assets/icons/PPSIcons/searchIcon.svg';
import { SetupTime } from 'Interfaces/ReferenceInterfaces/SetupTimeInterface';
import { observer } from 'mobx-react';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { InputText } from 'primereact/inputtext';
import { Menu } from 'primereact/menu';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useRef } from 'react';
import { commaSeparatedNumbers } from '../../../../utils/commaSeparatedNumbers';
import classes from './setupTimeTable.module.scss';
import { useSetupTimeTable } from './useSetupTimeTable';
import AddRowModalSetupTime from './utils/AddRowModalSetupTime';
import DeleteConfirmationModalSetupTime from './utils/DeleteConfirmationModalSetupTime';

interface SetupTimeTableProps {
  showAddButton?: boolean;
  showActionColumn?: boolean;
  showAllRows?: boolean;
}

const SetupTimeTable = observer(
  ({ showAddButton = true, showActionColumn = true, showAllRows = true }: SetupTimeTableProps) => {
    const pageSize = showAllRows ? 100000 : 100;
    const {
      setupTimeData,
      isLoading,
      pagination,
      itemMenu,
      selectedRow,
      showAddModal,
      showDeleteModal,
      setSelectedRow,
      setShowAddModal,
      setShowDeleteModal,
      handleAddRow,
      handleDeleteRow,
      prepareFormData,
      cleanTypeOptions,
      incomingJobTypeOptions,
      onPageChange,
      roomTypeOptions,
      searchQuery,
      handleSearchChange,
      handleExportToExcel
    } = useSetupTimeTable(pageSize);

    const searchInputRef = useRef<HTMLInputElement>(null);

    const menuItems = [
      {
        label: 'Edit Row',
        icon: <img src={editIcon} alt="Edit Row" style={{ marginRight: '0.5rem' }} />,
        command: () => setShowAddModal(true)
      },
      {
        label: 'Delete Row',
        icon: <img src={deleteIcon} alt="Delete Row" style={{ marginRight: '0.5rem' }} />,
        command: () => setShowDeleteModal(true)
      }
    ];

    const actionBodyTemplate = (rowData: SetupTime) => {
      return (
        <Button
          icon={<img src={actionIcon} alt="Action Icon" style={{ marginRight: '0.5rem' }} />}
          className={`p-button-rounded p-button-text p-button-sm ${classes.menuButton}`}
          onClick={e => {
            setSelectedRow(rowData);
            itemMenu.current?.toggle(e);
          }}
          aria-controls="popup_menu"
          aria-haspopup
        />
      );
    };

    const roomTemplate = (rowData: SetupTime) => {
      return `${rowData.Room_Type.description}`;
    };

    const cleanTypeTemplate = (rowData: SetupTime) => {
      return rowData.CleanType.description;
    };

    const incomingJobTypeTemplate = (rowData: SetupTime) => {
      return rowData.incoming_job_type.description;
    };

    const booleanTemplate = (rowData: SetupTime, field: keyof SetupTime) => {
      if (rowData[field] === null) {
        return 'N/A';
      }

      return rowData[field] ? 'Yes' : 'No';
    };

    const bottleCountMinBodyTemplate = (rowData: SetupTime) => {
      return commaSeparatedNumbers(rowData.bottle_count_min);
    };

    const bottleCountMaxBodyTemplate = (rowData: SetupTime) => {
      return commaSeparatedNumbers(rowData.bottle_count_max);
    };

    const setupTimeBodyTemplate = (rowData: SetupTime) => {
      return commaSeparatedNumbers(rowData.setup_time_minutes);
    };

    if (isLoading && !setupTimeData.length) {
      return (
        <div className={classes.loadingContainer}>
          <ProgressSpinner className={classes.spinner} />
        </div>
      );
    }

    return (
      <div className={classes.setupTimeTable}>
        <div className={classes.tableHeader}>
          <h2 className={classes.sectionTitle}>Setup Time Configuration</h2>
          <div className={classes.headerActions}>
            <div className={classes.searchContainer}>
              <span className={classes.searchIconSection}>
                <img src={searchIcon} alt="Search Icon" style={{ marginRight: '0.5rem' }} />
              </span>
              <InputText
                ref={searchInputRef}
                value={searchQuery}
                className={classes.searchInput}
                placeholder="Search..."
                onChange={e => handleSearchChange(e.target.value)}
              />
            </div>

            <Button
              icon="pi pi-download"
              className={`${classes.addButton} p-button-icon-only`}
              onClick={handleExportToExcel}
              tooltip="Export to Excel"
              tooltipOptions={{ position: 'bottom' }}
              style={{ color: 'white !important', fontSize: '1rem' }}
            />

            {showAddButton && (
              <Button
                label="Add New Row"
                icon="pi pi-plus"
                className={classes.addButton}
                onClick={() => {
                  setSelectedRow(null);
                  setShowAddModal(true);
                }}
              />
            )}
          </div>
        </div>

        <DataTable
          value={setupTimeData}
          showGridlines
          stripedRows
          emptyMessage="No records found"
          className={classes.dataTable}
          loading={isLoading}
          paginator={!showAllRows}
          rows={showAllRows ? setupTimeData.length : 100}
          totalRecords={pagination.totalItems}
          lazy
          first={(pagination.currentPage - 1) * 100}
          onPage={onPageChange}
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
        >
          <Column field="Room_Type" header="Room Type" body={roomTemplate} />
          <Column
            field="bottle_count_min"
            header="Bottle Count Min"
            body={bottleCountMinBodyTemplate}
          />
          <Column
            field="bottle_count_max"
            header="Bottle Count Max"
            body={bottleCountMaxBodyTemplate}
          />
          <Column
            field="incoming_job_type"
            header="Incoming Job Type"
            body={incomingJobTypeTemplate}
          />
          <Column
            field="is_incoming_job_and_outgoing_job_bottle_stock_code_same"
            header="Same Bottle Stock Code"
            body={rowData =>
              booleanTemplate(rowData, 'is_incoming_job_and_outgoing_job_bottle_stock_code_same')
            }
          />
          <Column
            field="is_incoming_job_and_outgoing_job_cap_stock_code_same"
            header="Same Cap Stock Code"
            body={rowData =>
              booleanTemplate(rowData, 'is_incoming_job_and_outgoing_job_cap_stock_code_same')
            }
          />
          <Column field="CleanType" header="Clean Type" body={cleanTypeTemplate} />
          <Column
            field="setup_time_minutes"
            header="Setup Time (mins)"
            body={setupTimeBodyTemplate}
          />
          {showActionColumn && (
            <Column
              body={actionBodyTemplate}
              style={{ width: '5rem', textAlign: 'center' }}
              headerStyle={{ width: '5rem' }}
            />
          )}
        </DataTable>

        <Menu
          model={menuItems}
          className={classes.actionMenu}
          popup
          ref={itemMenu}
          id="popup_menu"
        />

        <AddRowModalSetupTime
          visible={showAddModal}
          onHide={() => {
            setShowAddModal(false);
            setSelectedRow(null);
          }}
          onSubmit={handleAddRow}
          initialValues={selectedRow ? prepareFormData(selectedRow) : undefined}
          isEdit={!!selectedRow}
          roomOptions={roomTypeOptions}
          cleanTypeOptions={cleanTypeOptions}
          incomingJobTypeOptions={incomingJobTypeOptions}
        />

        <DeleteConfirmationModalSetupTime
          visible={showDeleteModal}
          onHide={() => {
            setShowDeleteModal(false);
            setSelectedRow(null);
          }}
          onConfirm={handleDeleteRow}
        />
      </div>
    );
  }
);

export default SetupTimeTable;
